import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Typography } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  useGetProfileBookingsQuery,
  useGetProfileDeskSchedulesQuery,
} from 'generated/graphql';
import { BookingsList, BookingsListSkeleton } from './BookingsList';
import {
  getActiveReservations,
  getReservationKind,
} from './utils/getReservationActions';
import { isDateInWeeklyRecurrence } from './utils/formatBookings';
import { useProfileDeskReservationActions } from './hooks/useProfileDeskReservationActions';

type BookingsListContainerProps = {
  userId: string;
  isProfileSidebarOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
  onNavigateAway?: () => void;
  /** Lay desk-card actions out in a row (fullscreen Desks panel). */
  wideLayout?: boolean;
};

export const BookingsListContainer = ({
  userId,
  isProfileSidebarOpen = true,
  onModalOpenChange,
  onNavigateAway,
  wideLayout = false,
}: BookingsListContainerProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings',
  });
  const currentTime = moment();

  const start = useMemo(
    () => currentTime.clone().startOf('day').toISOString(),
    [currentTime]
  );
  const end = useMemo(
    () => currentTime.clone().endOf('day').toISOString(),
    [currentTime]
  );

  const { data, loading, error } = useGetProfileBookingsQuery({
    variables: { userId, start, end },
    skip: !userId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const activeReservations = useMemo(
    () =>
      getActiveReservations(
        data?.getDeskReservationsByUserIds?.[0]?.reservations ?? [],
        currentTime
      ),
    [currentTime, data]
  );

  const assignmentDeskIds = useMemo(
    () =>
      activeReservations
        .filter(
          (reservation) =>
            getReservationKind(reservation) === 'assignment' &&
            !!reservation.seriesId
        )
        .map((reservation) => reservation.seat.id)
        .filter((deskId, index, deskIds) => deskIds.indexOf(deskId) === index),
    [activeReservations]
  );

  const { data: schedulesData, loading: schedulesLoading } =
    useGetProfileDeskSchedulesQuery({
      variables: { deskIds: assignmentDeskIds },
      skip: assignmentDeskIds.length === 0,
      errorPolicy: 'all',
    });

  const recurrenceByDeskId = useMemo(() => {
    const map: Record<string, string | null> = {};
    schedulesData?.getSharedDesksSchedules?.forEach((deskSchedule) => {
      if (!deskSchedule?.id) {
        return;
      }
      const userSchedule = deskSchedule.schedule?.find(
        (entry) => entry?.id === userId
      );
      map[deskSchedule.id] = userSchedule?.recurrence ?? null;
    });
    return map;
  }, [schedulesData, userId]);

  const reservations = useMemo(
    () =>
      activeReservations.filter((reservation) => {
        if (getReservationKind(reservation) !== 'assignment') {
          return true;
        }

        const recurrence = recurrenceByDeskId[reservation.seat.id];
        return isDateInWeeklyRecurrence(
          recurrence,
          currentTime,
          reservation.seat.location.timezone ?? reservation.timeZone
        );
      }),
    [activeReservations, currentTime, recurrenceByDeskId]
  );

  const { isCurrentPerson, onCardClick, onCheckIn, onMenuAction, modals } =
    useProfileDeskReservationActions({
      userId,
      isProfileSidebarOpen,
      onModalOpenChange,
      onNavigateAway,
      recurrenceByDeskId,
    });

  if ((loading && !data) || schedulesLoading) {
    return <BookingsListSkeleton />;
  }

  if (error && !data) {
    return (
      <ErrorWrapper>
        <Typography.Text type="secondary">{t('errorLoading')}</Typography.Text>
      </ErrorWrapper>
    );
  }

  return (
    <>
      <BookingsList
        reservations={reservations}
        isCurrentPerson={isCurrentPerson}
        recurrenceByDeskId={recurrenceByDeskId}
        currentTime={currentTime}
        onCardClick={onCardClick}
        onCheckIn={onCheckIn}
        onMenuAction={onMenuAction}
        wideLayout={wideLayout}
      />
      {modals}
    </>
  );
};

const ErrorWrapper = styled.div`
  padding: 0 16px 16px;
`;
